<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';
import countries from 'shared/constants/countries';
import { useBrowserPhoneStore } from 'dashboard/modules/browserPhone/store/browserPhone';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

const DEFAULT_COLUMNS = ['name', 'phone', 'responsibleAgent', 'company'];

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  visibleColumns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['toggleContact']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const agents = useMapGetter('agents/getVerifiedAgents');
const browserPhoneStore = useBrowserPhoneStore();

const selectedIdsSet = computed(
  () => new Set(props.selectedContactIds || [])
);

const visibleColumnsSet = computed(() => {
  const list =
    props.visibleColumns && props.visibleColumns.length
      ? props.visibleColumns
      : DEFAULT_COLUMNS;
  return new Set(list);
});

const agentNameById = computed(() => {
  const map = new Map();
  (agents.value || []).forEach(agent => map.set(Number(agent.id), agent.name));
  return map;
});

const countriesMap = computed(() =>
  countries.reduce((acc, country) => {
    acc[country.id] = country;
    acc[country.code] = country;
    return acc;
  }, {})
);

const SOCIAL_ICON_MAP = {
  facebook: { icon: 'brand-facebook', prefix: 'https://facebook.com/' },
  twitter: { icon: 'brand-twitter', prefix: 'https://twitter.com/' },
  linkedin: { icon: 'brand-linkedin', prefix: 'https://linkedin.com/' },
  github: { icon: 'brand-github', prefix: 'https://github.com/' },
  instagram: { icon: 'brand-instagram', prefix: 'https://instagram.com/' },
  telegram: { icon: 'brand-telegram', prefix: 'https://t.me/' },
  tiktok: { icon: 'brand-tiktok', prefix: 'https://tiktok.com/@' },
  line: { icon: 'brand-line', prefix: 'https://line.me/R/ti/p/~' },
  phone: { icon: 'call-outline', prefix: 'https://wa.me/' },
  website: { icon: 'globe', prefix: '' },
};

const formatTime = value => (value ? dynamicTime(value) : '—');

const getResponsibleAgentName = id => {
  if (!id) return '—';
  return agentNameById.value.get(Number(id)) || '—';
};

const getCountryDetails = additionalAttributes => {
  const countryCode =
    additionalAttributes?.countryCode || additionalAttributes?.country;
  const country = countryCode ? countriesMap.value[countryCode] : null;
  if (!country) return null;

  const city = additionalAttributes?.city
    ? `${additionalAttributes.city}, `
    : '';

  return {
    code: String(country.id).toLowerCase(),
    label: `${city}${country.name}`,
  };
};

const buildSocialLinks = profiles =>
  Object.entries(profiles || {})
    .filter(([, value]) => !!value)
    .map(([key, value]) => {
      const mapEntry = SOCIAL_ICON_MAP[key] || {
        icon: `brand-${key}`,
        prefix: '',
      };
      const safeValue = String(value);
      const href =
        mapEntry.prefix || safeValue.startsWith('http')
          ? `${mapEntry.prefix}${safeValue}`
          : `https://${safeValue}`;
      return {
        key,
        icon: mapEntry.icon,
        href,
      };
    });

const handleViewDetails = async id => {
  const routeTypes = {
    contacts_segments_dashboard: ['contacts_edit_segment', 'segmentId'],
    contacts_labels_dashboard: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || [
    'contact_profile_dashboard',
  ];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleContactSelection = (id, checked) => {
  emit('toggleContact', { id, value: checked });
};

const handleClickToDial = contact => {
  if (!contact.phoneNumber) return;

  browserPhoneStore.dial({
    source: 'contacts',
    number: contact.phoneNumber,
    contactId: contact.id,
    contactName: contact.name,
  });
};
</script>

<template>
  <div class="w-full overflow-x-auto">
    <table
      class="w-full min-w-[1100px] border-collapse text-sm text-n-slate-12"
    >
      <thead class="text-xs font-semibold uppercase text-n-slate-11">
        <tr class="border-b border-n-weak">
          <th class="w-10 px-4 lg:px-5 py-3 text-left"></th>
          <th
            v-if="visibleColumnsSet.has('name')"
            class="px-4 lg:px-5 py-3 text-left"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('email')"
            class="px-4 lg:px-5 py-3 text-left min-w-[14rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.EMAIL_ADDRESS') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('phone')"
            class="px-4 lg:px-5 py-3 text-left min-w-[9rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('responsibleAgent')"
            class="px-4 lg:px-5 py-3 text-left min-w-[10rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.RESPONSIBLE_AGENT') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('company')"
            class="px-4 lg:px-5 py-3 text-left min-w-[10rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.COMPANY') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('city')"
            class="px-4 lg:px-5 py-3 text-left min-w-[8rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.CITY') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('country')"
            class="px-4 lg:px-5 py-3 text-left min-w-[8rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.COUNTRY') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('socialProfiles')"
            class="px-4 lg:px-5 py-3 text-left min-w-[9rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.SOCIAL_PROFILES') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('lastActivity')"
            class="px-4 lg:px-5 py-3 text-left min-w-[9rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.LAST_ACTIVITY') }}
          </th>
          <th
            v-if="visibleColumnsSet.has('createdAt')"
            class="px-4 lg:px-5 py-3 text-left min-w-[9rem]"
          >
            {{ t('CONTACTS_PAGE.LIST.TABLE_HEADER.CREATED_AT') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <tr
          v-for="contact in contacts"
          :key="contact.id"
          class="transition-colors hover:bg-n-alpha-black2/40 border-b border-n-weak"
          :class="{ 'bg-n-alpha-black2': selectedIdsSet.has(contact.id) }"
        >
          <td class="px-4 lg:px-5 py-4 align-top">
            <Checkbox
              :model-value="selectedIdsSet.has(contact.id)"
              @change="event => toggleContactSelection(contact.id, event.target.checked)"
            />
          </td>
          <td
            v-if="visibleColumnsSet.has('name')"
            class="px-4 lg:px-5 py-4 align-top min-w-[16rem]"
          >
            <div class="flex items-start gap-3">
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="42"
                :status="contact.availabilityStatus"
                hide-offline-status
                rounded-full
              />
              <div class="flex flex-col gap-1 min-w-0">
                <div class="flex flex-col gap-1 min-w-0">
                  <span
                    class="text-sm font-semibold leading-tight truncate text-n-slate-12 max-w-[14rem]"
                  >
                    {{ contact.name || '—' }}
                  </span>
                  <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Button
                      variant="link"
                      size="xs"
                      icon="i-ph-identification-card size-4"
                      :title="t('CONTACTS_PAGE.LIST.VIEW_DETAILS')"
                      class="w-fit p-1 min-w-0"
                      @click="() => handleViewDetails(contact.id)"
                    />
                    <ComposeConversation
                      :contact-id="String(contact.id)"
                      is-modal
                    >
                      <template #trigger="{ toggle }">
                        <Button
                          variant="link"
                          size="xs"
                          icon="i-ph-chat-circle-dots size-4"
                          :title="t('CONTACTS_LAYOUT.HEADER.SEND_MESSAGE')"
                          class="w-fit p-1 min-w-0"
                          @click="toggle"
                        />
                      </template>
                    </ComposeConversation>
                    <Button
                      v-if="contact.phoneNumber"
                      variant="link"
                      size="xs"
                      icon="i-ph-phone size-4"
                      :title="t('CONTACT_PANEL.CALL')"
                      class="w-fit p-1 min-w-0"
                      @click="() => handleClickToDial(contact)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </td>
          <td
            v-if="visibleColumnsSet.has('email')"
            class="px-4 lg:px-5 py-4 align-top min-w-[14rem]"
          >
            <span class="block truncate text-n-slate-11">
              <a
                v-if="contact.email"
                :href="`mailto:${contact.email}`"
                class="text-n-blue-11 hover:underline"
              >
                {{ contact.email }}
              </a>
              <span v-else>—</span>
            </span>
          </td>
          <td
            v-if="visibleColumnsSet.has('phone')"
            class="px-4 lg:px-5 py-4 align-top text-n-slate-11 min-w-[9rem]"
          >
            {{ contact.phoneNumber || '—' }}
          </td>
          <td
            v-if="visibleColumnsSet.has('responsibleAgent')"
            class="px-4 lg:px-5 py-4 align-top text-n-slate-11 min-w-[10rem]"
          >
            {{
              getResponsibleAgentName(
                contact.additionalAttributes?.responsibleAgentId
              )
            }}
          </td>
          <td
            v-if="visibleColumnsSet.has('company')"
            class="px-4 lg:px-5 py-4 align-top text-n-slate-11 min-w-[10rem]"
          >
            {{ contact.additionalAttributes?.companyName || '—' }}
          </td>
          <td
            v-if="visibleColumnsSet.has('city')"
            class="px-4 lg:px-5 py-4 align-top text-n-slate-11 min-w-[8rem]"
          >
            {{ contact.additionalAttributes?.city || '—' }}
          </td>
          <td
            v-if="visibleColumnsSet.has('country')"
            class="px-4 lg:px-5 py-4 align-top min-w-[8rem]"
          >
            <div
              v-if="getCountryDetails(contact.additionalAttributes)"
              class="inline-flex items-center gap-2 text-n-slate-11"
            >
              <Flag
                :country="getCountryDetails(contact.additionalAttributes).code"
                class="size-4"
              />
              <span class="truncate">
                {{ getCountryDetails(contact.additionalAttributes).label }}
              </span>
            </div>
            <span v-else class="text-n-slate-11">—</span>
          </td>
          <td
            v-if="visibleColumnsSet.has('socialProfiles')"
            class="px-4 lg:px-5 py-4 align-top min-w-[9rem]"
          >
            <div class="flex flex-wrap items-center gap-2">
              <a
                v-for="item in buildSocialLinks(
                  contact.additionalAttributes?.socialProfiles
                )"
                :key="item.key"
                :href="item.href"
                target="_blank"
                rel="noopener noreferrer nofollow"
                class="text-n-slate-10 hover:text-n-slate-12"
              >
                <FluentIcon :icon="item.icon" size="14" />
              </a>
              <span
                v-if="!buildSocialLinks(contact.additionalAttributes?.socialProfiles).length"
                class="text-n-slate-11"
              >
                —
              </span>
            </div>
          </td>
          <td
            v-if="visibleColumnsSet.has('lastActivity')"
            class="px-4 lg:px-5 py-4 align-top text-n-slate-11 min-w-[9rem]"
          >
            {{ formatTime(contact.lastActivityAt) }}
          </td>
          <td
            v-if="visibleColumnsSet.has('createdAt')"
            class="px-4 lg:px-5 py-4 align-top text-n-slate-11 min-w-[9rem]"
          >
            {{ formatTime(contact.createdAt) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
